/**
 * Insights agregados Sprint 35.
 *
 * Vista admin que renderiza un radar Recharts con los 6 dominios
 * cyber (identify/protect/detect/respond/recover/compliance) +
 * tabla de detalle por plantilla. Reusa /api/v1/admin/dashboard.
 */
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  type DashboardData,
  type TrendData,
  adminDashboardApi,
  adminInsightsApi,
} from '../api/admin-dashboard.js';
import { useAppShell } from '../components/AppShell.js';
import {
  ButtonLink,
  Card,
  EmptyState,
  LoadingSkeleton,
  PageHeader,
  ScoreBar,
  scoreTextColor,
} from '../components/ui.js';

const CYBER_DOMAINS = [
  { id: 'identify', label: 'Identify' },
  { id: 'protect', label: 'Protect' },
  { id: 'detect', label: 'Detect' },
  { id: 'respond', label: 'Respond' },
  { id: 'recover', label: 'Recover' },
  { id: 'compliance', label: 'Compliance' },
] as const;

const TARGET_LEVEL = 80;

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

interface RadarPoint {
  domain: string;
  score: number;
  target: number;
  samples: number;
}

/**
 * Agrega `avg_per_domain` (multiples plantillas × dominio) en un
 * unico promedio ponderado por `samples` por dominio.
 */
export function aggregatePerDomain(
  rows: DashboardData['avg_per_domain'],
): Map<string, { weightedAverage: number; samples: number }> {
  const acc = new Map<string, { sum: number; samples: number }>();
  for (const row of rows) {
    const prev = acc.get(row.domain) ?? { sum: 0, samples: 0 };
    prev.sum += row.average * row.samples;
    prev.samples += row.samples;
    acc.set(row.domain, prev);
  }
  const out = new Map<string, { weightedAverage: number; samples: number }>();
  for (const [domain, { sum, samples }] of acc) {
    out.set(domain, {
      weightedAverage: samples > 0 ? sum / samples : 0,
      samples,
    });
  }
  return out;
}

function buildRadarData(rows: DashboardData['avg_per_domain']): RadarPoint[] {
  const agg = aggregatePerDomain(rows);
  return CYBER_DOMAINS.map(({ id, label }) => {
    const entry = agg.get(id);
    return {
      domain: label,
      score: entry ? Math.round(entry.weightedAverage) : 0,
      target: TARGET_LEVEL,
      samples: entry?.samples ?? 0,
    };
  });
}

function templateBreakdown(rows: DashboardData['avg_per_domain']): {
  template_code: string;
  domains: Record<string, { average: number; samples: number }>;
}[] {
  const byTemplate = new Map<string, Record<string, { average: number; samples: number }>>();
  for (const row of rows) {
    const tpl = byTemplate.get(row.template_code) ?? {};
    tpl[row.domain] = { average: row.average, samples: row.samples };
    byTemplate.set(row.template_code, tpl);
  }
  return Array.from(byTemplate.entries())
    .map(([template_code, domains]) => ({ template_code, domains }))
    .sort((a, b) => a.template_code.localeCompare(b.template_code));
}

const DOMAIN_COLORS: Record<string, string> = {
  identify: '#0ea5e9',
  protect: '#0d9488',
  detect: '#8b5cf6',
  respond: '#f97316',
  recover: '#84cc16',
  compliance: '#dc2626',
};

interface LineRow {
  bucketLabel: string;
  identify?: number;
  protect?: number;
  detect?: number;
  respond?: number;
  recover?: number;
  compliance?: number;
}

/**
 * Convierte `TrendData.buckets` en filas planas compatibles con
 * Recharts LineChart. `null` se mapea a `undefined` para que la
 * linea no caiga a 0 en buckets sin muestras.
 */
export function buildLineRows(trend: TrendData | null): LineRow[] {
  if (!trend) return [];
  return trend.buckets.map((b) => {
    const row: LineRow = { bucketLabel: b.start.slice(0, 10) };
    for (const domain of CYBER_DOMAINS) {
      const entry = b.per_domain[domain.id];
      if (entry && entry.samples > 0) {
        (row as unknown as Record<string, unknown>)[domain.id] = entry.average;
      }
    }
    return row;
  });
}

export function AdminInsightsPage(): JSX.Element {
  const { setBackTo } = useAppShell();

  // Back button (BB-UX-01)
  useEffect(() => {
    setBackTo({ href: '/admin/dashboard', label: 'Dashboard' });
    return () => setBackTo(null);
  }, [setBackTo]);

  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(isoDate(monthAgo));
  const [to, setTo] = useState(isoDate(today));
  const [data, setData] = useState<DashboardData | null>(null);
  const [trend, setTrend] = useState<TrendData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load(f: string, t: string): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const fromIso = new Date(`${f}T00:00:00Z`).toISOString();
      const toIso = new Date(`${t}T23:59:59Z`).toISOString();
      const [dashboard, trendData] = await Promise.all([
        adminDashboardApi.get({ from: fromIso, to: toIso }),
        adminInsightsApi.trend({ from: fromIso, to: toIso }),
      ]);
      setData(dashboard);
      setTrend(trendData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(from, to);
  }, []);

  const radar = useMemo(() => (data ? buildRadarData(data.avg_per_domain) : []), [data]);
  const breakdown = useMemo(() => (data ? templateBreakdown(data.avg_per_domain) : []), [data]);
  const lineRows = useMemo(() => buildLineRows(trend), [trend]);
  const trendDomains = useMemo(() => {
    if (!trend) return [];
    return CYBER_DOMAINS.filter(({ id }) => trend.domains.includes(id));
  }, [trend]);
  const hasData = radar.some((p) => p.samples > 0);
  const hasTrend = trend ? trend.buckets.some((b) => b.total_samples > 0) : false;

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    void load(from, to);
  }

  return (
    <main className="mx-auto max-w-7xl px-6 py-8 space-y-6">
      <PageHeader
        eyebrow="INSIGHTS"
        title="Cumplimiento por dominio cyber"
        subtitle="Radar agregado por los 6 dominios NIST CSF (identify, protect, detect, respond, recover, compliance). Promedio ponderado de assessments completados en el rango."
        actions={
          <div className="flex items-center gap-2">
            <a
              href={`/api/v1/admin/insights/export.pdf?from=${encodeURIComponent(
                new Date(`${from}T00:00:00Z`).toISOString(),
              )}&to=${encodeURIComponent(new Date(`${to}T23:59:59Z`).toISOString())}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md bg-brand-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-800"
            >
              Exportar PDF
            </a>
            <ButtonLink to="/admin/dashboard" variant="secondary" size="md">
              ← Dashboard
            </ButtonLink>
          </div>
        }
      />

      <form
        onSubmit={handleSubmit}
        className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4"
      >
        <label className="flex flex-col text-xs">
          <span className="font-medium text-slate-600">Desde</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          />
        </label>
        <label className="flex flex-col text-xs">
          <span className="font-medium text-slate-600">Hasta</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          />
        </label>
        <button
          type="submit"
          className="rounded-md bg-brand-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-800"
        >
          Aplicar
        </button>
      </form>

      {error && (
        <div
          role="alert"
          className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"
        >
          {error}
        </div>
      )}

      {loading && <LoadingSkeleton rows={4} />}

      {!loading && !error && data && (
        <>
          <Card
            title="Radar cumplimiento (6 dominios cyber)"
            hint={`Objetivo: ${TARGET_LEVEL}/100`}
          >
            {hasData ? (
              <div className="h-96 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radar} outerRadius="75%">
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="domain" tick={{ fill: '#475569', fontSize: 12 }} />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 100]}
                      tick={{ fill: '#94a3b8', fontSize: 10 }}
                    />
                    <Radar
                      name="Cumplimiento actual"
                      dataKey="score"
                      stroke="#0d9488"
                      fill="#0d9488"
                      fillOpacity={0.45}
                    />
                    <Radar
                      name="Objetivo"
                      dataKey="target"
                      stroke="#475569"
                      strokeDasharray="4 4"
                      fill="none"
                    />
                    <Tooltip
                      formatter={(v, name, item) => {
                        const payload = item.payload as RadarPoint | undefined;
                        const value = typeof v === 'number' ? v : Number(v);
                        const label = typeof name === 'string' ? name : String(name);
                        if (label === 'Cumplimiento actual' && payload) {
                          return [`${value}/100 (${payload.samples} muestras)`, label];
                        }
                        return [`${value}/100`, label];
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState
                title="Sin assessments completados"
                description="Aun no hay datos en el rango seleccionado. Completa assessments para ver el radar."
              />
            )}
          </Card>

          {hasData && (
            <>
              <Card
                title="Tendencia historica por dominio"
                {...(trend ? { hint: `Bucket: ${trend.bucket}` } : {})}
              >
                {hasTrend && lineRows.length > 0 ? (
                  <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={lineRows}
                        margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
                      >
                        <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                        <XAxis dataKey="bucketLabel" tick={{ fill: '#475569', fontSize: 11 }} />
                        <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {trendDomains.map(({ id, label }) => (
                          <Line
                            key={id}
                            type="monotone"
                            dataKey={id}
                            name={label}
                            stroke={DOMAIN_COLORS[id] ?? '#475569'}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState
                    title="Sin tendencia disponible"
                    description="No hay assessments completados en el rango para construir la serie historica."
                  />
                )}
              </Card>

              <Card title="Resumen por dominio">
                <div className="space-y-3">
                  {radar.map((row) => (
                    <ScoreBar
                      key={row.domain}
                      label={row.domain}
                      score={row.score}
                      hint={`${row.samples} muestras`}
                    />
                  ))}
                </div>
              </Card>
            </>
          )}

          {breakdown.length > 0 && (
            <Card title="Detalle por plantilla">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="text-left text-slate-600">
                    <tr>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Plantilla
                      </th>
                      {CYBER_DOMAINS.map((d) => (
                        <th key={d.id} scope="col" className="py-2 px-2 font-medium text-center">
                          {d.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {breakdown.map((row) => (
                      <tr key={row.template_code}>
                        <td className="py-2 pr-4 font-mono text-[11px] text-slate-700">
                          {row.template_code}
                        </td>
                        {CYBER_DOMAINS.map((d) => {
                          const cell = row.domains[d.id];
                          if (!cell) {
                            return (
                              <td key={d.id} className="py-2 px-2 text-center text-slate-300">
                                —
                              </td>
                            );
                          }
                          return (
                            <td
                              key={d.id}
                              className={`py-2 px-2 text-center font-semibold ${scoreTextColor(cell.average)}`}
                              title={`${cell.samples} muestras`}
                            >
                              {Math.round(cell.average)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </main>
  );
}
